import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { AiRequestDetails } from '@frontend/schedule-helpers';
import { PopoverMenuItem, Text, formatDate, useModalControl } from '@frontend/design-system';
import { StatusBadge } from '../../../../../../../components/status-badge';
import { useScheduleSettingsSmallMode } from '../../../../../../../hooks/settings/use-schedule-settings-small-mode';
import { useCheckCanSendMessages } from '../../hooks/use-check-can-send-messages';
import type { ScheduleRequestActionCalledFrom } from '../../hooks/use-schedule-request-actions';
import { useScheduleRequestItemHeaderController } from '../../hooks/use-schedule-request-item-header-controller';
import { ScheduleRequestsTrackingIds } from '../../trackingIds';
import { ScheduleRequestItemHeaderUI } from '../components/schedule-request-item-header-ui';

type Props = {
  requestVersion: string;
  name: string;
  appointmentTime: string;
  personPhone: string;
  locationId: string;
  aiRequestDetails?: AiRequestDetails;
  scheduleRequestId: string;
  isWidgetListItem?: boolean;
  patientContactedAt?: string;
  locationChip?: (locationName: string) => React.ReactNode;
  deleteRequestModalProps: ReturnType<typeof useModalControl>;
  dismissRequestModalProps: ReturnType<typeof useModalControl>;
  onCompleteRequest: (actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onDismissRequestIntent: (actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onMessage: (updateStatus: boolean, actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onPhoneCall: (actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
};

export const PreferenceBasedScheduleRequestItemHeader = ({
  requestVersion,
  name,
  appointmentTime,
  personPhone,
  locationId,
  scheduleRequestId,
  aiRequestDetails,
  isWidgetListItem,
  locationChip,
  patientContactedAt,
  deleteRequestModalProps,
  dismissRequestModalProps,
  onCompleteRequest,
  onDismissRequestIntent,
  onMessage,
  onPhoneCall,
}: Props) => {
  const { t } = useTranslation('schedule-request');
  const { isSmallMode } = useScheduleSettingsSmallMode();
  const isUrgentRequest = aiRequestDetails?.visitDetails?.priority === 'urgent';
  const canSendMessages = useCheckCanSendMessages(locationId);
  const showIconOnly = isSmallMode || !isWidgetListItem;

  const {
    locationName,
    appointmentTimeLabel,
    getMenuProps,
    getTriggerProps,
    handleViewRequestMenuItemClick,
    handleMessageMenuItemClick,
    handleCallMenuItemClick,
    handleDeleteMenuItemClick,
    handleDismissMenuItemClick,
    handleDoneMenuItemClick,
  } = useScheduleRequestItemHeaderController({
    requestVersion,
    locationId,
    scheduleRequestId,
    appointmentTime,
    isWidgetListItem,
    isPreferenceBased: true,
    deleteRequestModalProps,
    dismissRequestModalProps,
    onCompleteRequest,
    onDismissRequestIntent,
    onMessage,
    onPhoneCall,
  });

  const isPatientContacted = !!patientContactedAt;

  const primaryMeta = (
    <>
      <StatusBadge variant='ai-requested' iconOnly={showIconOnly} />
      {isUrgentRequest && <StatusBadge variant='urgent' />}
    </>
  );

  const secondaryMeta = (
    <>
      <Text textAlign='left' size='small'>
        {appointmentTimeLabel}
      </Text>
      {isPatientContacted && (
        <Text textAlign='left' size='small' color={'subdued'}>
          {t('Last Contacted on {{datetime}}', {
            datetime: formatDate(patientContactedAt, 'MMM D, YYYY, h:mm A'),
          })}
        </Text>
      )}
    </>
  );

  const menuItems = (
    <>
      {isWidgetListItem && (
        <PopoverMenuItem
          trackingId={ScheduleRequestsTrackingIds.requestListItemViewRequestBtn}
          Icon={() => <Icon name='lists-small' />}
          onClick={handleViewRequestMenuItemClick}
        >
          {t('View Request')}
        </PopoverMenuItem>
      )}
      {personPhone && (
        <PopoverMenuItem
          trackingId={ScheduleRequestsTrackingIds.requestListItemMessageBtn}
          Icon={() => <Icon name='message-small' />}
          disabled={!canSendMessages}
          hoverLabel={canSendMessages ? undefined : t('You do not have permission to send text messages')}
          onClick={handleMessageMenuItemClick}
        >
          {t('Message')}
        </PopoverMenuItem>
      )}
      {personPhone && (
        <PopoverMenuItem
          trackingId={ScheduleRequestsTrackingIds.requestListItemCallBtn}
          Icon={() => <Icon name='phone-small' />}
          onClick={handleCallMenuItemClick}
        >
          {t('Call')}
        </PopoverMenuItem>
      )}
      {isPatientContacted ? (
        <PopoverMenuItem
          trackingId={ScheduleRequestsTrackingIds.requestListItemDoneBtn}
          Icon={() => <Icon name='check-small' />}
          onClick={handleDoneMenuItemClick}
        >
          {t('Done')}
        </PopoverMenuItem>
      ) : (
        <PopoverMenuItem
          trackingId={ScheduleRequestsTrackingIds.requestListItemDismissBtn}
          Icon={() => <Icon name='x-small' />}
          onClick={handleDismissMenuItemClick}
        >
          {t('Dismiss')}
        </PopoverMenuItem>
      )}
      <PopoverMenuItem
        trackingId={ScheduleRequestsTrackingIds.requestListItemDeleteBtn}
        Icon={() => <Icon name='trash-small' />}
        onClick={handleDeleteMenuItemClick}
      >
        {t('Delete')}
      </PopoverMenuItem>
    </>
  );

  return (
    <ScheduleRequestItemHeaderUI
      locationName={locationName}
      name={name}
      primaryMeta={primaryMeta}
      secondaryMeta={secondaryMeta}
      menuItems={menuItems}
      locationChip={locationChip}
      getTriggerProps={getTriggerProps}
      getMenuProps={getMenuProps}
    />
  );
};
